import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { FieldEditForm } from "Components/UserAccount/Shared/PublicInformation/FieldEditForm";
import { usePartyFieldSave } from "Components/UserAccount/Shared/PublicInformation/usePartyFieldSave";
import type { PartyDto } from "Infrastructure/Api/Api";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { z } from "zod";

const createPhoneSchema = (t: TFunction) =>
  z.object({
    phone: getPhoneSchema(t, { isRequired: false }),
  });

type PhoneFormValues = z.infer<ReturnType<typeof createPhoneSchema>>;

type Props = {
  party: PartyDto;
  onSuccess: () => void;
};

/** Phone slice of the party contact edit (phone card's mini dialog). */
export const PhoneForm = ({ party, onSuccess }: Props) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<PhoneFormValues>({
    resolver: zodResolver(createPhoneSchema(t)),
    defaultValues: { phone: party.phone },
  });

  const { saveParty, isSaving } = usePartyFieldSave(party, onSuccess);

  if (!party.publicID) {
    return <ErrorText>{t(Resources.Errors.MissingId)}</ErrorText>;
  }

  return (
    <FieldEditForm
      onSubmit={handleSubmit((data) => saveParty({ phone: data.phone }))}
      isSubmitDisabled={!isDirty}
      isSaving={isSaving}
    >
      <PhoneInput
        control={control}
        name="phone"
        label={t(Resources.Common.Phone)}
        autoFocus
      />
    </FieldEditForm>
  );
};
